import { parseWithZod } from "@conform-to/zod";
import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { z } from "zod";

export const DEFAULT_DATE_FORMAT = "MMM D YYYY";

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

export function cleanUrl(url: string | null | undefined): string | null | undefined {
  if (!url) {
    return url;
  }

  return url.split("?")[0];
}

export function formatTime(time: number | undefined) {
  if (typeof time === "number" && !Number.isNaN(time)) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const formatMinutes = minutes.toString().padStart(2, "0");
    const formatSeconds = seconds.toString().padStart(2, "0");
    return `${formatMinutes}:${formatSeconds}`;
  }
  return "00:00";
}

/**
 * Convert 24-hour format hour to 12-hour format
 * @param hour24 - Hour in 24-hour format (0-23)
 * @returns Hour in 12-hour format (1-12)
 */
export function convert24HourTo12Hour(hour24: number): number {
  if (hour24 === 0) {
    return 12; // Midnight
  }
  if (hour24 > 12) {
    return hour24 - 12; // Afternoon
  }
  return hour24;
}

/**
 * Convert 12-hour format hour to 24-hour format
 * @param hour12 - Hour in 12-hour format (1-12)
 * @param meridiem - "AM" or "PM"
 * @returns Hour in 24-hour format (0-23)
 */
export function convert12HourTo24Hour(hour12: number, meridiem: string): number {
  const upperMeridiem = meridiem.toUpperCase();
  if (upperMeridiem === "PM" && hour12 !== 12) {
    return hour12 + 12;
  }
  if (upperMeridiem === "AM" && hour12 === 12) {
    return 0; // Midnight
  }
  return hour12;
}

/**
 * Convert API time format ("10:00:00" or "18:30:00") to display format ("7:00 AM" or "6:30 PM")
 * @param apiTime - Time in API format (HH:mm:ss)
 * @returns Time in display format (H:mm AM/PM) or null if invalid
 */
export function convertApiTimeToDisplayFormat(apiTime: string | null | undefined): string | null {
  if (!apiTime || typeof apiTime !== "string") {
    return null;
  }

  // Parse HH:mm:ss format
  const match = apiTime.match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!match || !match[1] || !match[2]) {
    return null;
  }

  const hour24 = parseInt(match[1], 10);
  const minute: string = match[2];
  const meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = convert24HourTo12Hour(hour24);

  return `${hour12}:${minute} ${meridiem}`;
}

/**
 * Check if a user has opted out of notifications via the app.
 * The optedOutViaApp field is a UTC ISO string (ending in "Z") when opted out, null otherwise.
 * @param optedOutViaApp - The user's optedOutViaApp field value
 * @returns true if the user has opted out, false otherwise
 */
export function isUserOptedOut(optedOutViaApp: string | null | undefined): boolean {
  return Boolean(optedOutViaApp && typeof optedOutViaApp === "string" && optedOutViaApp.endsWith("Z"));
}

/**
 * Convert display format ("7:00 AM" or "6:30 PM") to API format ("07:00:00" or "18:30:00")
 * @param displayTime - Time in display format (H:mm AM/PM)
 * @returns Time in API format (HH:mm:ss) or null if invalid
 */
export function convertDisplayFormatToApiFormat(displayTime: string | null | undefined): string | null {
  if (!displayTime || typeof displayTime !== "string") {
    return null;
  }

  const match = displayTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match || !match[1] || !match[2] || !match[3]) {
    return null;
  }

  const hour12 = parseInt(match[1], 10);
  const minute = match[2];
  const meridiem = match[3];
  const hour24 = convert12HourTo24Hour(hour12, meridiem);

  return `${hour24.toString().padStart(2, "0")}:${minute}:00`;
}

export async function parseConform<T extends z.ZodTypeAny>(schema: T, request: Request) {
  const formData = await request.clone().formData();
  const submission = parseWithZod<T>(formData, { schema });
  return submission;
}

export async function parseFormData<T extends z.ZodTypeAny>(schema: T, request: Request) {
  const formData = await request.clone().formData();
  const result = schema.safeParse(Object.fromEntries(formData));
  return result as z.SafeParseReturnType<FormData, z.infer<T>>;
}

export function formatPrice(price: number) {
  return Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

export function formatDate(date?: string | null, format: string = DEFAULT_DATE_FORMAT): string {
  if (!date) return "";

  const parsedDate = dayjs(date);

  if (!parsedDate.isValid()) return "";

  return parsedDate.format(format);
}

export function sanitizeUtmData(data: unknown, utmParams: ReadonlyArray<string>): Record<string, string | undefined> {
  const result: Record<string, string | undefined> = {};
  if (typeof data === "object" && data !== null) {
    for (const key of utmParams) {
      const value = (data as Record<string, unknown>)[key];
      result[key] = typeof value === "string" ? value : undefined;
    }
  } else {
    for (const key of utmParams) {
      result[key] = undefined;
    }
  }
  return result;
}

/**
 * Normalize a date to midnight (00:00:00.000)
 */
export function normalizeDateToMidnight(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function useDebouncedCallback<TArgs extends Array<unknown>>(callback: (...args: TArgs) => void, delay: number) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return (...args: TArgs) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}...`;
}

// Returns true if the window height is greater than the breakpoint (default: 700px)
export function useLargeHeightScreen(breakpoint = 700): boolean {
  const [isLarge, setIsLarge] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerHeight > breakpoint : false,
  );

  useEffect(() => {
    function handleResize() {
      setIsLarge(window.innerHeight > breakpoint);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isLarge;
}

export function useSmallWidthScreen(breakpoint = 750): boolean {
  const [isSmall, setIsSmall] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    function handleResize() {
      setIsSmall(window.innerWidth < breakpoint);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isSmall;
}

export function getBaseUrl() {
  if (typeof window !== "undefined") {
    if (window.location.host.includes(import.meta.env.VITE_PORT)) {
      return "https://sing.dev.theworshipinitiative.com/api";
    }
  }
  return import.meta.env.VITE_API_BASE_URL;
}

export function getDomainUrl() {
  if (typeof window !== "undefined") {
    if (window.location.host.includes(import.meta.env.VITE_PORT)) {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return `${window.location.protocol}//${window.location.host}`;
  }
}

// Helper: check if value is a string
export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function formatDollars(amount: number | null) {
  if (!amount) return "";
  const hasCents = amount % 1 !== 0;
  return `$${hasCents ? amount.toFixed(2) : amount.toFixed(0)}`;
}

export function formatPurchasedOn(dateString: string) {
  const date = new Date(`${dateString}T00:00:00`);
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  }).format(date);
}

export function normalizeInlineWhitespace(value: string): string {
  return value
    .replace(/\s*\n\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }

  return result as Partial<T>;
}
